import { readFingerprintCurrentStep } from './boardGoalFields.js';

export const SEARCH_PER_BATCH = 3;
export const SEARCH_CANDIDATES_MAX = 9;
export const GEMINI_CANDIDATES_MAX = 3;
/** Max steps returned from a single AI refresh pack. */
export const STEP_CANDIDATES_MAX = 5;
/** Max steps kept in the Do-next dropdown (AI suggestions only). */
export const STEP_CANDIDATES_LIST_MAX = 20;
/** Per-step child coach limits (alternate stepCandidates from Do-next refresh). */
export const STEP_CANDIDATE_BLOCKERS_MAX = 2;
export const STEP_CANDIDATE_ACTIONS_MAX = 3;
export const BLOCKERS_MAX = 3;
export const VALIDATION_QUEUE_MAX = 3;
export const NEXT_ACTIONS_MAX = 4;
export const DECIDE_OPTIONS_MAX = 4;

const IMPACT_RANK = { high: 0, medium: 1, low: 2 };

export function validationKeyFromClaim(claim) {
  const slug = String(claim || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\uac00-\ud7a3]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return slug || '';
}

export function normalizeWorkingConclusion(raw, fallback = {}) {
  const fromRaw = String(raw?.workingConclusion || '').trim().slice(0, 160);
  if (fromRaw) return fromRaw;
  return String(fallback.workingConclusion || '').trim().slice(0, 160);
}

export function normalizeValidationItem(item) {
  const claim = String(item?.claim || item?.title || item?.label || '').trim().slice(0, 160);
  if (!claim) return null;
  const key = String(item?.key || '').trim().slice(0, 80) || validationKeyFromClaim(claim);
  if (!key) return null;
  const impactRaw = String(item?.impactIfFails || item?.impact || 'medium').trim().toLowerCase();
  const impactIfFails = IMPACT_RANK[impactRaw] != null ? impactRaw : 'medium';
  let probeKind = String(item?.probeKind || item?.kind || '').trim().toLowerCase();
  const query = String(item?.query || '').trim().slice(0, 120);
  const question = String(item?.question || item?.prompt || '').trim().slice(0, 500);
  if (probeKind !== 'search' && probeKind !== 'ask') {
    probeKind = question && !query ? 'ask' : 'search';
  }
  if (probeKind === 'search' && !query) return null;
  if (probeKind === 'ask' && !question) return null;
  return {
    key,
    claim,
    whyItMatters: String(item?.whyItMatters || item?.why || item?.reason || '').trim().slice(0, 200),
    impactIfFails,
    probeKind,
    query: probeKind === 'search' ? query : '',
    question: probeKind === 'ask' ? question : '',
    blockerTitle: String(item?.blockerTitle || '').trim().slice(0, 120),
  };
}

export function collectValidationQueue(raw, fallback = {}) {
  const items = [];
  const seen = new Set();
  const add = entry => {
    const normalized = normalizeValidationItem(entry);
    if (!normalized || seen.has(normalized.key)) return;
    seen.add(normalized.key);
    items.push(normalized);
  };
  const sources = [
    ...(Array.isArray(raw?.validationQueue) ? raw.validationQueue : []),
    ...(Array.isArray(fallback.validationQueue) ? fallback.validationQueue : []),
  ];
  for (const item of sources) {
    if (typeof item === 'string') add({ claim: item, probeKind: 'search', query: item });
    else add(item || {});
  }
  items.sort((a, b) => (IMPACT_RANK[a.impactIfFails] ?? 1) - (IMPACT_RANK[b.impactIfFails] ?? 1));
  return items.slice(0, VALIDATION_QUEUE_MAX);
}

const NEXT_ACTION_KINDS = new Set([
  'search',
  'ask',
  'place',
  'decide',
  'set-goal',
  'add-text',
  'organize',
]);

export function topicLabelFromHashtag(hashtag) {
  if (!hashtag || hashtag === 'no_hashtag') return 'this board';
  return String(hashtag).replace(/^#/, '');
}

function normalizeImageUrl(url) {
  const trimmed = String(url || '').trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.href;
  } catch {
    return '';
  }
  return '';
}

function normalizeSearchCandidate(query, reason, imageUrl, blockerTitle) {
  const q = String(query || '').trim().slice(0, 120);
  if (!q) return null;
  const entry = {
    query: q,
    reason: String(reason || '').trim().slice(0, 200),
  };
  const normalizedImageUrl = normalizeImageUrl(imageUrl);
  if (normalizedImageUrl) entry.imageUrl = normalizedImageUrl;
  const linkedBlocker = String(blockerTitle || '').trim().slice(0, 120);
  if (linkedBlocker) entry.blockerTitle = linkedBlocker;
  return entry;
}

function normalizeGeminiCandidate(question, reason, imageUrl, blockerTitle) {
  const q = String(question || '').trim().slice(0, 500);
  if (!q) return null;
  const entry = {
    question: q,
    reason: String(reason || '').trim().slice(0, 200),
  };
  const normalizedImageUrl = normalizeImageUrl(imageUrl);
  if (normalizedImageUrl) entry.imageUrl = normalizedImageUrl;
  const linkedBlocker = String(blockerTitle || '').trim().slice(0, 120);
  if (linkedBlocker) entry.blockerTitle = linkedBlocker;
  return entry;
}

function collectSearchQueryCandidates(raw, fallback = {}) {
  const candidates = [];
  const seen = new Set();

  const add = (query, reason, imageUrl, blockerTitle, { prepend = false } = {}) => {
    const entry = normalizeSearchCandidate(query, reason, imageUrl, blockerTitle);
    if (!entry) return;
    const key = entry.query.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    if (prepend) candidates.unshift(entry);
    else candidates.push(entry);
  };

  if (Array.isArray(raw?.nextActions)) {
    for (const action of [...raw.nextActions].reverse()) {
      if (action?.kind === 'search' && action?.query) {
        add(action.query, action.why || action.label, action.imageUrl, action.blockerTitle, { prepend: true });
      }
    }
  }

  if (Array.isArray(raw?.searchQueryCandidates)) {
    for (const item of raw.searchQueryCandidates) {
      if (typeof item === 'string') add(item, '');
      else add(item?.query, item?.reason, item?.imageUrl, item?.blockerTitle);
    }
  }

  if (Array.isArray(raw?.searchQueries)) {
    for (const item of raw.searchQueries) {
      if (typeof item === 'string') add(item, '');
      else add(item?.query, item?.reason, item?.imageUrl, item?.blockerTitle);
    }
  }

  add(raw?.searchQuery, raw?.searchReason);

  if (!candidates.length && Array.isArray(fallback.nextActions)) {
    for (const action of [...fallback.nextActions].reverse()) {
      if (action?.kind === 'search' && action?.query) {
        add(action.query, action.why || action.label, action.imageUrl, action.blockerTitle, { prepend: true });
      }
    }
  }

  if (!candidates.length && Array.isArray(fallback.searchQueryCandidates)) {
    for (const item of fallback.searchQueryCandidates) {
      add(item?.query, item?.reason, item?.imageUrl, item?.blockerTitle);
    }
  }

  if (!candidates.length && Array.isArray(fallback.searchQueries)) {
    for (const item of fallback.searchQueries) {
      add(item?.query, item?.reason, item?.imageUrl, item?.blockerTitle);
    }
  }

  return candidates.slice(0, SEARCH_CANDIDATES_MAX);
}

function collectGeminiQuestionCandidates(raw, fallback = {}) {
  const candidates = [];
  const seen = new Set();

  const add = (question, reason, imageUrl, blockerTitle, { prepend = false } = {}) => {
    const entry = normalizeGeminiCandidate(question, reason, imageUrl, blockerTitle);
    if (!entry) return;
    const key = entry.question.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    if (prepend) candidates.unshift(entry);
    else candidates.push(entry);
  };

  if (Array.isArray(raw?.nextActions)) {
    for (const action of [...raw.nextActions].reverse()) {
      if (action?.kind === 'ask' && action?.question) {
        add(action.question, action.why || action.label, action.imageUrl, action.blockerTitle, { prepend: true });
      }
    }
  }

  if (Array.isArray(raw?.geminiQuestionCandidates)) {
    for (const item of raw.geminiQuestionCandidates) {
      if (typeof item === 'string') add(item, '');
      else add(item?.question, item?.reason, item?.imageUrl, item?.blockerTitle);
    }
  }

  add(raw?.geminiQuestion, raw?.geminiQuestionReason, raw?.geminiImageUrl);

  if (!candidates.length && Array.isArray(fallback.nextActions)) {
    for (const action of [...fallback.nextActions].reverse()) {
      if (action?.kind === 'ask' && action?.question) {
        add(action.question, action.why || action.label, action.imageUrl, action.blockerTitle, { prepend: true });
      }
    }
  }

  if (!candidates.length && Array.isArray(fallback.geminiQuestionCandidates)) {
    for (const item of fallback.geminiQuestionCandidates) {
      add(item?.question, item?.reason, item?.imageUrl, item?.blockerTitle);
    }
  }

  if (!candidates.length) {
    add(fallback.geminiQuestion, fallback.geminiQuestionReason, fallback.geminiImageUrl);
  }

  return candidates.slice(0, GEMINI_CANDIDATES_MAX);
}

function normalizeStepCandidate(item) {
  if (typeof item === 'string') {
    const g = String(item || '').trim().slice(0, 200);
    return g ? { goal: g, reason: '', priority: 1 } : null;
  }
  const g = String(item?.goal || item?.text || '').trim().slice(0, 200);
  if (!g) return null;
  const rawPriority = Number(item?.priority);
  const entry = {
    goal: g,
    reason: String(item?.reason || '').trim().slice(0, 200),
    priority: Number.isFinite(rawPriority)
      ? Math.max(1, Math.min(99, Math.round(rawPriority)))
      : 1,
  };

  // Per-step child coach from Do-next refresh — selecting a dropdown step swaps
  // these into the top-level coach without another AI call.
  const childBlockers = collectBlockers(item, {}).slice(0, STEP_CANDIDATE_BLOCKERS_MAX);
  const childActions = collectNextActions(item, {}).slice(0, STEP_CANDIDATE_ACTIONS_MAX);
  const childSearch = collectSearchQueryCandidates(item, {});
  const childAsk = collectGeminiQuestionCandidates(item, {});
  const progressSummary = String(item?.progressSummary || '').trim().slice(0, 240);
  const achievementRate = normalizeAchievementRate(item, {});
  if (
    childBlockers.length
    || childActions.length
    || childSearch.length
    || childAsk.length
    || progressSummary
  ) {
    entry.blockers = childBlockers;
    entry.nextActions = childActions;
    entry.gaps = gapsFromBlockers(childBlockers, item?.gaps);
    if (childSearch.length) entry.searchQueryCandidates = childSearch;
    if (childAsk.length) entry.geminiQuestionCandidates = childAsk;
    if (progressSummary) entry.progressSummary = progressSummary;
    if (achievementRate != null) entry.achievementRate = achievementRate;
  }

  return entry;
}

/** Normalize AI stepPriorities ranking (in-progress + new candidates). */
export function normalizeStepPriorities(raw) {
  const rows = [];
  const seen = new Set();
  const source = Array.isArray(raw)
    ? raw
    : (Array.isArray(raw?.stepPriorities) ? raw.stepPriorities : []);
  for (const item of source) {
    const goal = String(item?.goal || item?.text || '').trim().slice(0, 200);
    if (!goal) continue;
    const key = goal.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const priority = Number(item?.priority);
    const progressSummary = String(item?.progressSummary || item?.reason || '').trim().slice(0, 240);
    const row = {
      goal,
      priority: Number.isFinite(priority)
        ? Math.max(1, Math.min(99, Math.round(priority)))
        : rows.length + 1,
    };
    if (progressSummary) row.progressSummary = progressSummary;
    rows.push(row);
  }
  return rows
    .sort((a, b) => a.priority - b.priority)
    .map((row, index) => ({ ...row, priority: index + 1 }))
    .slice(0, 40);
}

/**
 * Apply AI stepPriorities onto stepCandidates, then sort and re-index 1..N.
 * Also overlays fresh why-first progressSummary from the ranking (refresh).
 */
export function applyStepPrioritiesToStepCandidates(stepCandidates, stepPriorities) {
  const list = Array.isArray(stepCandidates) ? stepCandidates : [];
  if (!list.length) return [];
  const byKey = new Map();
  for (const row of normalizeStepPriorities(stepPriorities)) {
    const key = String(row?.goal || '').trim().toLowerCase();
    if (!key) continue;
    byKey.set(key, row);
  }
  // Ranked steps occupy priorities 1..byKey.size; unranked AI steps that the
  // model omitted from stepPriorities are pushed after all ranked ones.
  const rankedMax = byKey.size;
  let unrankedOffset = 0;
  return list
    .map((item) => {
      const entry = normalizeStepCandidate(item);
      if (!entry) return null;
      const ranking = byKey.get(entry.goal.toLowerCase());
      const fromRanking = Number(ranking?.priority);
      if (Number.isFinite(fromRanking)) {
        entry.priority = fromRanking;
      } else if (rankedMax > 0) {
        // No AI ranking for this step — place it after all ranked steps.
        unrankedOffset += 1;
        entry.priority = rankedMax + unrankedOffset;
      }
      const freshSummary = String(ranking?.progressSummary || '').trim().slice(0, 240);
      if (freshSummary) {
        entry.progressSummary = freshSummary;
        if (!String(entry.reason || '').trim()) {
          entry.reason = freshSummary.slice(0, 200);
        }
      }
      return entry;
    })
    .filter(Boolean)
    .sort((a, b) => a.priority - b.priority)
    .map((item, index) => ({ ...item, priority: index + 1 }));
}

function collectStepCandidates(raw, fallback = {}) {
  const candidates = [];
  const seen = new Set();

  const add = (item) => {
    const entry = normalizeStepCandidate(item);
    if (!entry) return;
    const key = entry.goal.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    // Preserve AI priority; fall back to arrival order.
    if (!Number.isFinite(Number(item?.priority))) {
      entry.priority = candidates.length + 1;
    }
    candidates.push(entry);
  };

  if (Array.isArray(raw?.stepCandidates)) {
    for (const item of raw.stepCandidates) add(item);
  }

  if (!candidates.length && Array.isArray(fallback.stepCandidates)) {
    for (const item of fallback.stepCandidates) add(item);
  }

  const ranked = applyStepPrioritiesToStepCandidates(
    candidates,
    raw?.stepPriorities || fallback?.stepPriorities
  );
  // Persist/concatenate may hold more than one AI pack.
  return ranked.slice(0, STEP_CANDIDATES_LIST_MAX);
}

function normalizeAchievementRate(raw, fallback = {}) {
  const value = Number(raw?.achievementRate);
  if (Number.isFinite(value)) {
    return Math.max(0, Math.min(100, Math.round(value)));
  }
  const fallbackValue = Number(fallback?.achievementRate);
  if (Number.isFinite(fallbackValue)) {
    return Math.max(0, Math.min(100, Math.round(fallbackValue)));
  }
  return null;
}

function normalizeProgressSummary(raw, fallback = {}) {
  const summary = String(raw?.progressSummary || '').trim().slice(0, 240);
  if (summary) return summary;
  return String(fallback?.progressSummary || '').trim().slice(0, 240);
}

function normalizeBlocker(item) {
  if (typeof item === 'string') {
    const title = item.trim().slice(0, 120);
    if (!title) return null;
    return { title, why: '', howToFill: '' };
  }
  const title = String(item?.title || item?.text || item?.gap || '').trim().slice(0, 120);
  if (!title) return null;
  const entry = {
    title,
    why: String(item?.why || '').trim().slice(0, 200),
    howToFill: String(item?.howToFill || item?.how || '').trim().slice(0, 200),
  };
  const facetId = String(item?.facetId || '').trim().slice(0, 40);
  if (facetId) entry.facetId = facetId;
  const coverageStatus = String(item?.coverageStatus || '').trim().toLowerCase();
  if (['missing', 'thin', 'covered'].includes(coverageStatus)) {
    entry.coverageStatus = coverageStatus;
  }
  if (Array.isArray(item?.clusterIds) && item.clusterIds.length) {
    entry.clusterIds = item.clusterIds.map(id => String(id || '').trim()).filter(Boolean).slice(0, 6);
  }
  if (Array.isArray(item?.exemplarPinIds) && item.exemplarPinIds.length) {
    entry.exemplarPinIds = item.exemplarPinIds.map(id => String(id || '').trim()).filter(Boolean).slice(0, 5);
  }
  if (Array.isArray(item?.clusterLabels) && item.clusterLabels.length) {
    entry.clusterLabels = item.clusterLabels.map(l => String(l || '').trim()).filter(Boolean).slice(0, 4);
  }
  return entry;
}

function collectBlockers(raw, fallback = {}) {
  const blockers = [];
  const seen = new Set();

  const add = item => {
    const entry = normalizeBlocker(item);
    if (!entry) return;
    const key = entry.title.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    blockers.push(entry);
  };

  if (Array.isArray(raw?.blockers)) {
    for (const item of raw.blockers) add(item);
  }

  if (!blockers.length && Array.isArray(raw?.gaps)) {
    for (const gap of raw.gaps) add(gap);
  }

  if (!blockers.length && Array.isArray(fallback.blockers)) {
    for (const item of fallback.blockers) add(item);
  }

  if (!blockers.length && Array.isArray(fallback.gaps)) {
    for (const gap of fallback.gaps) add(gap);
  }

  return blockers.slice(0, BLOCKERS_MAX);
}

function normalizeNextActionOption(item) {
  if (typeof item === 'string') {
    const label = item.trim().slice(0, 80);
    return label ? { label, kind: '', query: '', question: '', text: '', goal: '' } : null;
  }
  const label = String(item?.label || item?.text || item?.goal || '').trim().slice(0, 80);
  if (!label) return null;
  const kindRaw = String(item?.kind || item?.type || '').trim().toLowerCase();
  let kind = NEXT_ACTION_KINDS.has(kindRaw) ? kindRaw : '';
  const query = String(item?.query || '').trim().slice(0, 120);
  const question = String(item?.question || '').trim().slice(0, 500);
  const text = String(item?.text || '').trim().slice(0, 400);
  let goal = String(item?.goal || '').trim().slice(0, 200);

  // Organize is not a Do-next chip or decide-option job.
  if (kind === 'organize') kind = '';

  // Spurious goal on non-set-goal options used to rewrite the current goal on click.
  if (kind !== 'set-goal') goal = '';
  else if (!goal) goal = label;

  return {
    label,
    kind,
    query,
    question,
    text,
    goal,
  };
}

function coerceNextActionKind(kind, { query, question, text, goal, pinQueries }) {
  if (kind === 'set-goal') {
    // Mis-tagged set-goal with another payload and no goal text → real action kind.
    if (!goal && query) return 'search';
    if (!goal && question) return 'ask';
    if (!goal && text) return 'search';
    if (!goal && pinQueries?.length) return 'search';
    return 'set-goal';
  }
  return kind;
}

function normalizePinQueries(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const item of raw) {
    const q = String(typeof item === 'string' ? item : item?.query || '').trim().slice(0, 120);
    if (!q) continue;
    const key = q.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(q);
    if (out.length >= 3) break;
  }
  return out;
}

function normalizeNextAction(item, index = 0) {
  if (typeof item === 'string') {
    const label = item.trim().slice(0, 120);
    if (!label) return null;
    return {
      label,
      why: '',
      priority: index + 1,
      kind: 'decide',
      query: '',
      question: '',
      text: '',
      command: '',
      goal: '',
      pinQueries: [],
      options: [],
      blockerTitle: '',
    };
  }
  const label = String(item?.label || item?.action || item?.text || '').trim().slice(0, 120);
  if (!label) return null;
  const kindRaw = String(item?.kind || item?.type || 'decide').trim().toLowerCase();
  const priority = Number(item?.priority);
  const options = Array.isArray(item?.options)
    ? item.options.map(normalizeNextActionOption).filter(Boolean).slice(0, DECIDE_OPTIONS_MAX)
    : [];
  const query = String(item?.query || '').trim().slice(0, 120);
  const question = String(item?.question || item?.prompt || '').trim().slice(0, 500);
  const text = String(item?.text || item?.note || '').trim().slice(0, 400);
  let goal = String(item?.goal || '').trim().slice(0, 200);
  const pinQueries = normalizePinQueries(item?.pinQueries || item?.queries);
  let kind = coerceNextActionKind(
    NEXT_ACTION_KINDS.has(kindRaw) ? kindRaw : 'decide',
    { query, question, text, goal, pinQueries }
  );
  // Organize is a canvas side-effect, not a Do-next action.
  if (kind === 'organize') return null;
  if (kind !== 'set-goal') goal = '';
  else if (!goal) goal = label;
  const activityId = String(item?.activityId || item?.targetPinId || '').trim().slice(0, 120);

  return {
    label,
    why: String(item?.why || item?.reason || '').trim().slice(0, 200),
    priority: Number.isFinite(priority) ? Math.max(1, Math.min(99, Math.round(priority))) : index + 1,
    kind,
    query,
    question,
    text,
    command: String(item?.command || '').trim().slice(0, 400),
    goal,
    pinQueries,
    options,
    blockerTitle: String(item?.blockerTitle || '').trim().slice(0, 120),
    activityId,
  };
}

function collectNextActions(raw, fallback = {}) {
  const actions = [];
  const seen = new Set();

  const add = (item, index) => {
    const entry = normalizeNextAction(item, index);
    if (!entry) return;
    const key = entry.label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    actions.push(entry);
  };

  if (Array.isArray(raw?.nextActions)) {
    raw.nextActions.forEach((item, index) => add(item, index));
  }

  if (!actions.length && Array.isArray(fallback.nextActions)) {
    fallback.nextActions.forEach((item, index) => add(item, index));
  }

  const capped = actions
    .sort((a, b) => a.priority - b.priority)
    .slice(0, NEXT_ACTIONS_MAX);
  // Last job may be decide, ask, or search. Do not move ask/search after decide.
  const last = capped[capped.length - 1];
  const lastKind = String(last?.kind || '').trim().toLowerCase();
  if (
    capped.length
    && last
    && lastKind !== 'ask'
    && lastKind !== 'search'
    && lastKind !== 'decide'
  ) {
    const groundIdx = capped.findIndex(a => a.kind === 'ask' || a.kind === 'search');
    if (groundIdx >= 0) {
      const [ground] = capped.splice(groundIdx, 1);
      capped.push(ground);
    }
  }
  return capped.map((action, index) => ({ ...action, priority: index + 1 }));
}

/**
 * set-goal → stepCandidates only; add-text / place / organize stripped from Do next
 * (no auto canvas draw / no auto suggest-pins / no auto organize).
 */
function divertNextActionSideEffects(nextActions, stepCandidates) {
  const kept = [];
  const goals = Array.isArray(stepCandidates) ? [...stepCandidates] : [];
  const seenGoals = new Set(
    goals.map(item => String(item?.goal || '').trim().toLowerCase()).filter(Boolean)
  );

  for (const action of nextActions || []) {
    if (action?.kind === 'set-goal') {
      const goal = String(action.goal || action.label || '').trim().slice(0, 200);
      if (goal) {
        const key = goal.toLowerCase();
        if (!seenGoals.has(key)) {
          seenGoals.add(key);
          goals.push({
            goal,
            reason: String(action.why || 'Suggested next step').trim().slice(0, 200),
          });
        }
      }
      continue;
    }
    if (action?.kind === 'add-text' || action?.kind === 'place' || action?.kind === 'organize') continue;
    kept.push(action);
  }

  return {
    nextActions: kept.map((action, index) => ({ ...action, priority: index + 1 })),
    stepCandidates: goals.slice(0, STEP_CANDIDATES_LIST_MAX),
  };
}

function gapsFromBlockers(blockers, rawGaps, fallbackGaps) {
  const fromBlockers = (blockers || []).map(b => b.title).filter(Boolean);
  if (fromBlockers.length) return fromBlockers.slice(0, 5);
  if (Array.isArray(rawGaps) && rawGaps.length) {
    return rawGaps
      .map(gap => (typeof gap === 'string' ? gap : gap?.title || gap?.text || ''))
      .map(gap => String(gap || '').trim())
      .filter(Boolean)
      .slice(0, 5);
  }
  if (Array.isArray(fallbackGaps)) {
    return fallbackGaps
      .map(gap => (typeof gap === 'string' ? gap : gap?.title || ''))
      .map(gap => String(gap || '').trim())
      .filter(Boolean)
      .slice(0, 5);
  }
  return [];
}

export function getContextRecommendationBatchCount(recs) {
  const searchQueryCandidates = recs?.searchQueryCandidates?.length
    ? recs.searchQueryCandidates
    : recs?.searchQueries || [];
  const geminiQuestionCandidates = recs?.geminiQuestionCandidates?.length
    ? recs.geminiQuestionCandidates
    : recs?.geminiQuestion
      ? [{
        question: recs.geminiQuestion,
        reason: recs.geminiQuestionReason || '',
        imageUrl: recs.geminiImageUrl || '',
      }]
      : [];

  const searchBatches = Math.ceil(searchQueryCandidates.length / SEARCH_PER_BATCH);
  const geminiBatches = geminiQuestionCandidates.length;
  const batches = Math.max(searchBatches, geminiBatches, 1);
  return batches > 1 ? batches : 1;
}

export function applyContextRecommendationBatch(recs, batchIndex = 0) {
  if (!recs) return recs;

  const searchQueryCandidates = recs.searchQueryCandidates?.length
    ? recs.searchQueryCandidates
    : recs.searchQueries || [];
  const geminiQuestionCandidates = recs.geminiQuestionCandidates?.length
    ? recs.geminiQuestionCandidates
    : recs.geminiQuestion
      ? [{
        question: recs.geminiQuestion,
        reason: recs.geminiQuestionReason || '',
        imageUrl: recs.geminiImageUrl || '',
      }]
      : [];

  const safeBatchIndex = Math.max(0, Number(batchIndex) || 0);
  const searchStart = safeBatchIndex * SEARCH_PER_BATCH;
  const searchQueries = searchQueryCandidates.slice(searchStart, searchStart + SEARCH_PER_BATCH);
  const gemini = geminiQuestionCandidates[safeBatchIndex] || geminiQuestionCandidates[0] || {};

  return {
    ...recs,
    searchQueryCandidates,
    geminiQuestionCandidates,
    suggestionBatchCount: getContextRecommendationBatchCount(recs),
    suggestionBatchIndex: safeBatchIndex,
    searchQueries,
    searchQuery: searchQueries[0]?.query || '',
    searchReason: searchQueries[0]?.reason || '',
    geminiQuestion: gemini.question || '',
    geminiQuestionReason: gemini.reason || '',
    geminiImageUrl: gemini.imageUrl || '',
    stepCandidates: Array.isArray(recs.stepCandidates) ? recs.stepCandidates : [],
    achievementRate: Number.isFinite(Number(recs.achievementRate))
      ? Math.max(0, Math.min(100, Math.round(Number(recs.achievementRate))))
      : null,
    progressSummary: String(recs.progressSummary || '').trim(),
    workingConclusion: String(recs.workingConclusion || '').trim().slice(0, 160),
    validationQueue: Array.isArray(recs.validationQueue)
      ? recs.validationQueue.slice(0, VALIDATION_QUEUE_MAX)
      : [],
    blockers: Array.isArray(recs.blockers) ? recs.blockers.slice(0, BLOCKERS_MAX) : [],
    nextActions: Array.isArray(recs.nextActions) ? recs.nextActions.slice(0, NEXT_ACTIONS_MAX) : [],
    gaps: Array.isArray(recs.gaps) ? recs.gaps.slice(0, 5) : [],
  };
}

export function buildFallbackContextRecommendations({
  hashtag,
  goal,
  description,
  currentStep,
  boardGoal,
  locale = 'en',
} = {}) {
  const topic = topicLabelFromHashtag(hashtag);
  const goalText = String(currentStep || goal || '').trim();
  const descriptionText = String(boardGoal || description || '').trim();
  const isKo = String(locale || 'en').toLowerCase().startsWith('ko');

  const searchQueryCandidates = [
    {
      query: (isKo ? `${topic} 일정 체크리스트` : `${topic} itinerary checklist`).slice(0, 120),
      reason: isKo
        ? '빠진 구체적인 계획 정보를 찾기'
        : 'Find concrete planning details that may be missing',
    },
  ];

  let geminiQuestion = goalText
    ? (isKo
      ? `보드 목표를 향한 현재 단계 "${goalText}"를 기준으로, 지금 ${topic} 보드에서 가장 중요한 빈틈은 무엇인가요?`
      : `Given my current step "${goalText}" toward my board goal, what is the most important gap on my ${topic} board right now?`)
    : (isKo
      ? `${topic} 보드에서 보드 목표를 향한 가장 좋은 다음 단계는 무엇인가요?`
      : `What is the best next step toward my board goal on my ${topic} board?`);

  if (descriptionText) {
    geminiQuestion = `${geminiQuestion}\n\n${isKo ? '보드 목표' : 'Board goal'}: ${descriptionText.slice(0, 300)}`;
  }

  const geminiQuestionCandidates = [{
    question: geminiQuestion.slice(0, 500),
    reason: isKo
      ? '보드 목표를 향한 다음 단계를 정하는 데 도움이 됩니다'
      : 'Helps decide the next step toward the board goal',
  }];

  const stepCandidates = [];
  if (goalText) {
    stepCandidates.push({
      goal: goalText.slice(0, 200),
      reason: isKo ? '보드 목표를 향한 현재 단계' : 'Your current step toward the board goal',
    });
  }
  if (descriptionText) {
    stepCandidates.push({
      goal: (isKo
        ? `${topic}의 다음 이정표 명확히 하기`
        : `Clarify the next milestone for ${topic}`).slice(0, 200),
      reason: isKo ? '보드 목표 아래의 구체적인 단계' : 'A concrete step under the board goal',
    });
    stepCandidates.push({
      goal: (isKo
        ? `${topic}의 최우선 빈틈 결정하기`
        : `Decide the highest-priority gap for ${topic}`).slice(0, 200),
      reason: isKo
        ? '보드 목표를 다음 실행 단계로 바꾸기'
        : 'Turn the board goal into the next actionable step',
    });
  } else {
    stepCandidates.push({
      goal: (isKo
        ? `${topic}의 첫 이정표 정하기`
        : `Define the first milestone for ${topic}`).slice(0, 200),
      reason: isKo ? '보드 목표가 있을 때의 시작 단계' : 'Starter step once a board goal exists',
    });
    stepCandidates.push({
      goal: (isKo
        ? `${topic}에서 가장 큰 빈틈 찾기`
        : `Identify the biggest gap on ${topic}`).slice(0, 200),
      reason: isKo ? '다음에 조사할 내용 찾기' : 'Find what to research next',
    });
  }

  const progressSummary = goalText
    ? (isKo
      ? `보드 목표를 향해 "${goalText.slice(0, 80)}" 단계를 진행 중입니다.`
      : `Working the step "${goalText.slice(0, 80)}" toward the board goal.`)
    : descriptionText
      ? (isKo
        ? `다음 현재 단계를 선택하세요: ${descriptionText.slice(0, 60)}.`
        : `Pick a current step toward: ${descriptionText.slice(0, 60)}.`)
      : (isKo
        ? `보드 목표를 정한 뒤, 이 ${topic} 보드의 현재 단계를 선택하세요.`
        : `Set a board goal, then pick a current step for this ${topic} board.`);
  const blockers = goalText
    ? [{
      title: isKo ? '관련 핀이 더 필요함' : 'Need more supporting pins',
      why: isKo
        ? '현재 단계는 설정됐지만 아직 커버리지가 얇습니다'
        : 'This current step is set but coverage is still thin',
      howToFill: isKo
        ? '검색하거나 질문해서 보드 목표를 향한 이 단계를 끝낼 핀을 모으세요'
        : 'Search or ask to gather pins that finish this step toward the board goal',
    }]
    : [{
      title: isKo ? '아직 현재 단계가 없음' : 'No current step yet',
      why: isKo
        ? '현재 단계가 없으면 다음 행동이 보드 목표 대비 너무 넓어집니다'
        : 'Without a current step, next actions stay broad vs the board goal',
      howToFill: isKo
        ? '추천에서 현재 단계를 선택하세요'
        : 'Pick a current step from the suggestions',
    }];
  const nextActions = goalText
    ? [
      {
        label: isKo
          ? `부족한 ${topic} 세부정보 검색`
          : `Search for missing ${topic} details`,
        why: isKo
          ? '이 현재 단계의 가장 큰 빈틈을 채우기'
          : 'Fill the biggest gap for this current step',
        priority: 1,
        kind: 'search',
        query: searchQueryCandidates[0].query,
        question: '',
        text: '',
        command: '',
        goal: '',
        pinQueries: [],
        options: [],
      },
      {
        label: isKo ? '관련 자료 더 검색' : 'Search for more related info',
        why: isKo
          ? '현재 단계를 위한 추가 정보 찾기'
          : 'Find additional information for the current step',
        priority: 2,
        kind: 'search',
        query: isKo
          ? `${goalText.slice(0, 60)} 관련 정보`
          : `${goalText.slice(0, 60)} related information`,
        question: '',
        text: '',
        command: '',
        goal: '',
        pinQueries: [],
        options: [],
      },
    ]
    : [
      {
        label: isKo ? '현재 단계 설정' : 'Set a current step',
        why: isKo
          ? '보드 목표 아래의 다음 이정표 고르기'
          : 'Pick the next milestone under the board goal',
        priority: 1,
        kind: 'set-goal',
        query: '',
        question: isKo
          ? '보드 목표를 향해 지금 집중할 다음 단계는 무엇인가요?'
          : 'Which next step toward the board goal should we focus on now?',
        text: '',
        command: '',
        goal: stepCandidates[0]?.goal || '',
        pinQueries: [],
        options: stepCandidates.slice(0, 3).map(item => ({
          label: item.goal,
          kind: 'set-goal',
          query: '',
          question: '',
          text: '',
          goal: item.goal,
        })),
      },
      {
        label: isKo
          ? `부족한 ${topic} 세부정보 검색`
          : `Search for missing ${topic} details`,
        why: isKo
          ? '현재 단계가 정해지면 집중 검색부터 시작'
          : 'Start with a focused search once a current step is clear',
        priority: 2,
        kind: 'search',
        query: searchQueryCandidates[0].query,
        question: '',
        text: '',
        command: '',
        goal: '',
        pinQueries: [],
        options: [],
      },
    ];

  return applyContextRecommendationBatch({
    searchQueryCandidates,
    geminiQuestionCandidates,
    stepCandidates: stepCandidates.slice(0, STEP_CANDIDATES_MAX),
    achievementRate: goalText ? 10 : 0,
    progressSummary,
    blockers,
    nextActions,
    gaps: blockers.map(b => b.title),
  });
}

export function normalizeNotInterestedGoalKeys(rawKeys) {
  const seen = new Set();
  const out = [];
  for (const item of Array.isArray(rawKeys) ? rawKeys : []) {
    const key = String(item || '').trim();
    if (!key) continue;
    const lower = key.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(key);
    if (out.length >= 40) break;
  }
  return out;
}

/** Prefer notInterestedGoalKeys; migrate legacy dismissedGoalKeys. */
export function readNotInterestedGoalKeys(userProgress) {
  if (!userProgress || typeof userProgress !== 'object') return [];
  return normalizeNotInterestedGoalKeys([
    ...(Array.isArray(userProgress.notInterestedGoalKeys)
      ? userProgress.notInterestedGoalKeys
      : []),
    ...(Array.isArray(userProgress.dismissedGoalKeys)
      ? userProgress.dismissedGoalKeys
      : []),
  ]);
}

/**
 * Steps hidden from the dropdown: not-interested keys only.
 * Finished steps stay in the list with a strikethrough.
 */
export function collectHiddenGoalSteps({ userProgress = null } = {}) {
  return readNotInterestedGoalKeys(userProgress);
}

/**
 * Finished Do-next steps from goalHistory. Excludes not-interested rows so
 * Canvas settings can list completed vs dismissed separately.
 */
export function collectCompletedGoalSteps({
  goalHistory = [],
  userProgress = null,
} = {}) {
  const hidden = new Set(
    readNotInterestedGoalKeys(userProgress).map(key => key.toLowerCase())
  );
  const out = [];
  const seen = new Set();
  for (const item of Array.isArray(goalHistory) ? goalHistory : []) {
    if (String(item?.status || '').trim().toLowerCase() !== 'finished') continue;
    const goal = String(item?.goal || '').trim();
    if (!goal) continue;
    const key = goal.toLowerCase();
    if (hidden.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(goal);
    if (out.length >= 40) break;
  }
  return out;
}

export function filterNotInterestedStepCandidates(candidates, notInterestedKeys) {
  const hidden = new Set(
    normalizeNotInterestedGoalKeys(notInterestedKeys).map(key => key.toLowerCase())
  );
  if (!hidden.size) return Array.isArray(candidates) ? candidates : [];
  return (Array.isArray(candidates) ? candidates : []).filter((item) => {
    const goal = typeof item === 'string' ? item : item?.goal;
    const key = String(goal || '').trim().toLowerCase();
    return key && !hidden.has(key);
  });
}

/** Cosine 0–1 or already-percent → 1–99 (partial never reads as fully done). */
export function clampPartialFillPercent(score) {
  const n = Number(score);
  if (!Number.isFinite(n) || n <= 0) return 50;
  const pct = n <= 1 ? Math.round(n * 100) : Math.round(n);
  return Math.max(1, Math.min(99, pct));
}

/** Highest partial-fill percent for a still-missing blocker, or null if none. */
export function partialPercentForBlocker(partialFills, blockerKey, blockerTitle) {
  const needles = [blockerKey, blockerTitle]
    .map(item => String(item || '').trim().toLowerCase())
    .filter(Boolean);
  if (!needles.length) return null;
  let best = null;
  for (const fill of Array.isArray(partialFills) ? partialFills : []) {
    const partialKey = String(fill?.partialKey || '').trim().toLowerCase();
    if (!partialKey || !needles.includes(partialKey)) continue;
    const pct = clampPartialFillPercent(fill?.score);
    if (best == null || pct > best) best = pct;
  }
  return best;
}

function normalizePartialFillsClient(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const indexByDedupe = new Map();
  for (const entry of raw) {
    const activityId = String(entry?.activityId || '').trim();
    const shapeId = String(entry?.shapeId || '').trim();
    const partialKey = String(entry?.partialKey || '').trim();
    if ((!activityId && !shapeId) || !partialKey) continue;
    const idKey = activityId || `shape:${shapeId}`;
    const dedupe = `${idKey.toLowerCase()}|${partialKey.toLowerCase()}`;
    const score = Number(entry?.score);
    const normalized = { partialKey };
    if (activityId) normalized.activityId = activityId;
    if (shapeId) normalized.shapeId = shapeId;
    if (Number.isFinite(score) && score > 0) {
      normalized.score = score > 1 ? Math.min(1, score / 100) : Math.min(1, score);
    }
    const existingIdx = indexByDedupe.get(dedupe);
    if (existingIdx != null) {
      const prevScore = Number(out[existingIdx]?.score);
      const nextScore = Number(normalized.score);
      if (Number.isFinite(nextScore) && (!Number.isFinite(prevScore) || nextScore > prevScore)) {
        out[existingIdx] = normalized;
      }
      continue;
    }
    if (out.length >= 24) continue;
    indexByDedupe.set(dedupe, out.length);
    out.push(normalized);
  }
  return out;
}

export function normalizeUserProgress(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const doneBlockerKeys = Array.isArray(raw.doneBlockerKeys)
    ? raw.doneBlockerKeys.map(item => String(item || '').trim()).filter(Boolean).slice(0, 12)
    : [];
  const doneValidationKeys = Array.isArray(raw.doneValidationKeys)
    ? raw.doneValidationKeys.map(item => String(item || '').trim()).filter(Boolean).slice(0, 12)
    : [];
  const doneFills = Array.isArray(raw.doneFills)
    ? raw.doneFills
      .map(entry => {
        const activityId = String(entry?.activityId || '').trim();
        const shapeId = String(entry?.shapeId || '').trim();
        const doneKey = String(entry?.doneKey || '').trim();
        if ((!activityId && !shapeId) || !doneKey) return null;
        return {
          ...(activityId ? { activityId } : {}),
          ...(shapeId ? { shapeId } : {}),
          doneKey,
          kind: entry?.kind === 'validation' ? 'validation' : 'blocker',
        };
      })
      .filter(Boolean)
      .slice(0, 24)
    : [];
  const partialBlockerKeys = Array.isArray(raw.partialBlockerKeys)
    ? raw.partialBlockerKeys.map(item => String(item || '').trim()).filter(Boolean).slice(0, 12)
    : [];
  const partialFills = normalizePartialFillsClient(raw.partialFills);
  const notInterestedKeys = Array.isArray(raw.notInterestedKeys)
    ? raw.notInterestedKeys.map(item => String(item || '').trim()).filter(Boolean).slice(0, 12)
    : [];
  const triedActionKeys = Array.isArray(raw.triedActionKeys)
    ? raw.triedActionKeys.map(item => String(item || '').trim()).filter(Boolean).slice(0, 20)
    : [];
  const notInterestedGoalKeys = readNotInterestedGoalKeys(raw);
  const aiAchievementRate = Number.isFinite(Number(raw.aiAchievementRate))
    ? Math.max(0, Math.min(100, Math.round(Number(raw.aiAchievementRate))))
    : null;
  if (
    !doneBlockerKeys.length
    && !doneValidationKeys.length
    && !doneFills.length
    && !partialBlockerKeys.length
    && !partialFills.length
    && !notInterestedKeys.length
    && !triedActionKeys.length
    && !notInterestedGoalKeys.length
    && aiAchievementRate == null
  ) {
    return null;
  }
  return {
    doneBlockerKeys,
    doneValidationKeys,
    doneFills,
    partialBlockerKeys,
    partialFills,
    notInterestedKeys,
    triedActionKeys,
    notInterestedGoalKeys,
    aiAchievementRate,
  };
}

function collectDeletedSourceIds(deleted = {}) {
  const pinIds = new Set();
  const shapeIds = new Set();
  const events = Array.isArray(deleted) ? deleted : (deleted?.events || []);
  for (const ev of events) {
    const pinId = String(ev?.pinId || ev?.activityId || '').trim();
    const shapeId = String(ev?.shapeId || '').trim();
    if (pinId) pinIds.add(pinId);
    if (shapeId) shapeIds.add(shapeId);
  }
  if (!Array.isArray(deleted)) {
    for (const id of deleted?.activityIds || []) {
      const pinId = String(id || '').trim();
      if (pinId) pinIds.add(pinId);
    }
    for (const id of deleted?.shapeIds || []) {
      const shapeId = String(id || '').trim();
      if (shapeId) shapeIds.add(shapeId);
    }
  }
  return { pinIds, shapeIds };
}

function fillMatchesDeletedSource(entry, pinIds, shapeIds) {
  const activityId = String(entry?.activityId || '').trim();
  const shapeId = String(entry?.shapeId || '').trim();
  if (activityId && pinIds.has(activityId)) return true;
  if (shapeId && shapeIds.has(shapeId)) return true;
  return false;
}

function filterKeysRemovingUncovered(keys, removedLower, stillCoveredLower) {
  return (Array.isArray(keys) ? keys : [])
    .map(key => String(key || '').trim())
    .filter(Boolean)
    .filter((key) => {
      const lower = key.toLowerCase();
      if (!removedLower.has(lower)) return true;
      return stillCoveredLower.has(lower);
    });
}

/**
 * Drop done/partial fills sourced by deleted pins/shapes.
 * Keys stay if another remaining fill still covers the same gap.
 */
export function pruneFillsForDeletedSources(userProgress, deleted = {}) {
  const { pinIds, shapeIds } = collectDeletedSourceIds(deleted);
  const progress = userProgress && typeof userProgress === 'object' ? userProgress : {};
  if (!pinIds.size && !shapeIds.size) {
    return { userProgress: progress, pruned: false };
  }

  const normalized = normalizeUserProgress(progress) || {
    doneBlockerKeys: [],
    doneValidationKeys: [],
    doneFills: [],
    partialBlockerKeys: [],
    partialFills: [],
    notInterestedKeys: [],
    triedActionKeys: [],
    notInterestedGoalKeys: [],
    aiAchievementRate: null,
  };
  const doneFills = Array.isArray(normalized.doneFills) ? normalized.doneFills : [];
  const partialFills = Array.isArray(normalized.partialFills) ? normalized.partialFills : [];
  const keptDone = doneFills.filter(entry => !fillMatchesDeletedSource(entry, pinIds, shapeIds));
  const removedDone = doneFills.filter(entry => fillMatchesDeletedSource(entry, pinIds, shapeIds));
  const keptPartial = partialFills.filter(entry => !fillMatchesDeletedSource(entry, pinIds, shapeIds));
  const removedPartial = partialFills.filter(entry => fillMatchesDeletedSource(entry, pinIds, shapeIds));
  if (!removedDone.length && !removedPartial.length) {
    return { userProgress: normalized, pruned: false };
  }

  const removedBlockerKeys = new Set(
    removedDone
      .filter(entry => entry.kind !== 'validation')
      .map(entry => String(entry.doneKey).toLowerCase())
  );
  const keptBlockerKeys = new Set(
    keptDone
      .filter(entry => entry.kind !== 'validation')
      .map(entry => String(entry.doneKey).toLowerCase())
  );
  const removedValidationKeys = new Set(
    removedDone
      .filter(entry => entry.kind === 'validation')
      .map(entry => String(entry.doneKey).toLowerCase())
  );
  const keptValidationKeys = new Set(
    keptDone
      .filter(entry => entry.kind === 'validation')
      .map(entry => String(entry.doneKey).toLowerCase())
  );
  const removedPartialKeys = new Set(
    removedPartial.map(entry => String(entry.partialKey).toLowerCase())
  );
  const keptPartialKeys = new Set(
    keptPartial.map(entry => String(entry.partialKey).toLowerCase())
  );

  const next = {
    ...normalized,
    doneFills: keptDone,
    partialFills: keptPartial,
    doneBlockerKeys: filterKeysRemovingUncovered(
      normalized.doneBlockerKeys,
      removedBlockerKeys,
      keptBlockerKeys
    ),
    doneValidationKeys: filterKeysRemovingUncovered(
      normalized.doneValidationKeys,
      removedValidationKeys,
      keptValidationKeys
    ),
    partialBlockerKeys: filterKeysRemovingUncovered(
      normalized.partialBlockerKeys,
      removedPartialKeys,
      keptPartialKeys
    ),
  };
  return {
    userProgress: normalizeUserProgress(next) || next,
    pruned: true,
  };
}

function unionProgressKeys(left = [], right = [], max = 12) {
  const out = [];
  const seen = new Set();
  for (const raw of [...left, ...right]) {
    const key = String(raw || '').trim();
    if (!key) continue;
    const norm = key.toLowerCase();
    if (seen.has(norm)) continue;
    seen.add(norm);
    out.push(key);
    if (out.length >= max) break;
  }
  return out;
}

function unionDoneFills(left = [], right = []) {
  const out = [];
  const seen = new Set();
  for (const entry of [...left, ...right]) {
    const activityId = String(entry?.activityId || '').trim();
    const shapeId = String(entry?.shapeId || '').trim();
    const doneKey = String(entry?.doneKey || '').trim();
    if ((!activityId && !shapeId) || !doneKey) continue;
    const idKey = activityId || `shape:${shapeId}`;
    const kind = entry?.kind === 'validation' ? 'validation' : 'blocker';
    const dedupe = `${idKey.toLowerCase()}|${doneKey.toLowerCase()}|${kind}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    out.push({
      ...(activityId ? { activityId } : {}),
      ...(shapeId ? { shapeId } : {}),
      doneKey,
      kind,
    });
    if (out.length >= 24) break;
  }
  return out;
}

/**
 * Union two coach userProgress snapshots. Soft pin-case fills must not clobber
 * live marks, and persist-sync must not drop incoming done keys when keeping a
 * richer live coach pack.
 */
export function mergeCoachUserProgress(prevProgress, incomingProgress) {
  const prev = normalizeUserProgress(prevProgress);
  const incoming = normalizeUserProgress(incomingProgress);
  if (!prev && !incoming) return null;
  if (!incoming) return prev;
  if (!prev) return incoming;

  const doneBlockerKeys = unionProgressKeys(
    prev.doneBlockerKeys,
    incoming.doneBlockerKeys,
    12
  );
  const doneSet = new Set(doneBlockerKeys.map(key => key.toLowerCase()));
  const partialBlockerKeys = unionProgressKeys(
    prev.partialBlockerKeys,
    incoming.partialBlockerKeys,
    12
  ).filter(key => !doneSet.has(key.toLowerCase()));

  return normalizeUserProgress({
    doneBlockerKeys,
    doneValidationKeys: unionProgressKeys(
      prev.doneValidationKeys,
      incoming.doneValidationKeys,
      12
    ),
    doneFills: unionDoneFills(prev.doneFills, incoming.doneFills),
    partialBlockerKeys,
    partialFills: [...(prev.partialFills || []), ...(incoming.partialFills || [])],
    notInterestedKeys: unionProgressKeys(
      prev.notInterestedKeys,
      incoming.notInterestedKeys,
      12
    ),
    triedActionKeys: unionProgressKeys(
      prev.triedActionKeys,
      incoming.triedActionKeys,
      20
    ),
    notInterestedGoalKeys: unionProgressKeys(
      prev.notInterestedGoalKeys,
      incoming.notInterestedGoalKeys,
      24
    ),
    aiAchievementRate: incoming.aiAchievementRate ?? prev.aiAchievementRate,
  });
}

/**
 * Soft-only pin-case: keep the live Do-next pack (current step blockers /
 * child actions) and patch gap progress onto it. Replacing the live coach with
 * the server copy drops the selected step's missing-parts UI.
 */
export function applySoftProgressToLiveCoach(liveRecs, incomingRecs, softProgress = null) {
  const live = liveRecs && typeof liveRecs === 'object' ? liveRecs : null;
  const incoming = incomingRecs && typeof incomingRecs === 'object' ? incomingRecs : null;
  if (!live && !incoming) return null;

  const incomingProgress = incoming?.userProgress || (
    softProgress && typeof softProgress === 'object'
      ? {
        doneBlockerKeys: softProgress.doneBlockerKeys,
        doneValidationKeys: softProgress.doneValidationKeys,
        doneFills: softProgress.doneFills,
        partialBlockerKeys: softProgress.partialBlockerKeys,
        partialFills: softProgress.partialFills,
        notInterestedKeys: softProgress.notInterestedKeys,
        aiAchievementRate: softProgress.aiAchievementRate,
      }
      : null
  );
  const base = live || incoming;
  const blockers = Array.isArray(base.blockers) ? base.blockers : [];
  const mergedProgress = mergeCoachUserProgress(base.userProgress, incomingProgress);
  const doneKeys = new Set(
    (mergedProgress?.doneBlockerKeys || []).map(key => String(key || '').trim().toLowerCase())
  );
  const fillKeys = (mergedProgress?.doneFills || [])
    .map(fill => String(fill?.doneKey || '').trim().toLowerCase())
    .filter(Boolean);
  for (const key of fillKeys) doneKeys.add(key);

  // Map incoming titles onto the live blocker keys (same as server applySoftProgress).
  const nextDone = new Set(mergedProgress?.doneBlockerKeys || []);
  blockers.forEach((blocker, index) => {
    const key = String(blocker?.title || '').trim() || `blocker:${index}`;
    const title = String(blocker?.title || '').trim().toLowerCase();
    if (doneKeys.has(key.toLowerCase()) || (title && doneKeys.has(title))) {
      nextDone.add(key);
    }
  });
  const userProgress = mergedProgress
    ? { ...mergedProgress, doneBlockerKeys: [...nextDone].slice(0, 12) }
    : mergedProgress;
  const doneForFilter = new Set(
    [...nextDone].map(key => String(key || '').trim().toLowerCase())
  );

  const nextActions = Array.isArray(base.nextActions)
    ? base.nextActions.filter((action) => {
      const blockerTitle = String(action?.blockerTitle || '').trim().toLowerCase();
      if (blockerTitle && doneForFilter.has(blockerTitle)) return false;
      return true;
    })
    : base.nextActions;

  const gaps = Array.isArray(base.gaps)
    ? base.gaps.filter(gap => !doneForFilter.has(String(gap || '').trim().toLowerCase()))
    : base.gaps;

  const incomingRate = Number(incoming?.achievementRate ?? softProgress?.aiAchievementRate);
  return {
    ...base,
    nextActions,
    gaps,
    userProgress,
    achievementRate: Number.isFinite(incomingRate)
      ? incomingRate
      : base.achievementRate,
  };
}

/**
 * Other pins that soft-filled the same listed gap (doneKey) as activityId.
 * Prefer an explicit doneKey (from softProgress.matchedKey); otherwise look it up.
 */
export function siblingActivityIdsForDoneKey(doneFills, activityId, doneKey = null) {
  const id = String(activityId || '').trim();
  if (!id || !Array.isArray(doneFills) || !doneFills.length) return [];

  let key = String(doneKey || '').trim();
  if (!key) {
    const self = doneFills.find(entry => String(entry?.activityId || '').trim() === id);
    key = String(self?.doneKey || '').trim();
  }
  if (!key) return [];

  const keyNorm = key.toLowerCase();
  const siblings = [];
  const seen = new Set();
  for (const entry of doneFills) {
    const peerId = String(entry?.activityId || '').trim();
    const peerKey = String(entry?.doneKey || '').trim().toLowerCase();
    if (!peerId || peerId === id || peerKey !== keyNorm || seen.has(peerId)) continue;
    seen.add(peerId);
    siblings.push(peerId);
  }
  return siblings;
}

/** Stable board.frames id for the frame that groups pins filling one missing part. */
export function doneGapFrameId(doneKey) {
  const slug = String(doneKey || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\uac00-\ud7a3]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return `done-gap-${slug || 'gap'}`;
}

/** Frame title = the missing-part label (blocker / validation claim). */
export function doneGapFrameLabel(doneKey) {
  return String(doneKey || '').trim().slice(0, 80) || 'Done';
}

export function normalizeDoneGapFrameName(name) {
  return String(name || '').trim().toLowerCase();
}

/** True when a frame name/label refers to this missing part. */
export function frameNameMatchesDoneKey(name, doneKey) {
  const label = normalizeDoneGapFrameName(doneGapFrameLabel(doneKey));
  const frameName = normalizeDoneGapFrameName(name);
  if (!label || !frameName) return false;
  if (frameName === label) return true;
  // Allow "Done · Budget" / "완료 · Budget" style titles.
  if (frameName.endsWith(` · ${label}`) || frameName.endsWith(` - ${label}`)) return true;
  return false;
}

/** Local coach progress from completing still-missing gaps (client-side bumps). */
export function computeGoalProgressRate(aiBaseRate, doneCount, blockerCount, {
  bumpPerBlocker = 5,
  cap = 95,
} = {}) {
  const safeBase = Number.isFinite(Number(aiBaseRate)) ? Math.max(0, Number(aiBaseRate)) : 0;
  const done = Math.max(0, Number(doneCount) || 0);
  if (!done) {
    return Math.min(cap, Math.max(0, Math.round(safeBase)));
  }
  const blockers = Math.max(0, Number(blockerCount) || 0);
  const room = Math.max(0, cap - safeBase);
  const perGap = blockers
    ? Math.max(bumpPerBlocker, Math.floor(room / blockers))
    : bumpPerBlocker;
  return Math.min(cap, Math.round(safeBase + (done * perGap)));
}

export function normalizeContextRecommendations(raw, fallback = {}) {
  const blockers = collectBlockers(raw, fallback);
  let nextActions = collectNextActions(raw, fallback);
  const searchQueryCandidates = collectSearchQueryCandidates(
    { ...raw, nextActions },
    { ...fallback, nextActions: fallback.nextActions || nextActions }
  );
  const geminiQuestionCandidates = collectGeminiQuestionCandidates(
    { ...raw, nextActions },
    { ...fallback, nextActions: fallback.nextActions || nextActions }
  );
  let stepCandidates = collectStepCandidates(raw, fallback);
  const diverted = divertNextActionSideEffects(nextActions, stepCandidates);
  nextActions = diverted.nextActions;
  stepCandidates = diverted.stepCandidates;
  const achievementRate = normalizeAchievementRate(raw, fallback);
  const progressSummary = normalizeProgressSummary(raw, fallback);
  const workingConclusion = normalizeWorkingConclusion(raw, fallback);
  const validationQueue = collectValidationQueue(raw, fallback);
  const gaps = gapsFromBlockers(blockers, raw?.gaps, fallback.gaps);
  const userProgress = normalizeUserProgress(raw?.userProgress)
    || normalizeUserProgress(fallback?.userProgress);
  stepCandidates = filterNotInterestedStepCandidates(
    stepCandidates,
    userProgress?.notInterestedGoalKeys
  );

  return applyContextRecommendationBatch({
    searchQueryCandidates,
    geminiQuestionCandidates,
    stepCandidates,
    achievementRate,
    progressSummary,
    workingConclusion,
    validationQueue,
    blockers,
    nextActions,
    gaps,
    userProgress,
    goalFacets: raw?.goalFacets || fallback?.goalFacets || null,
    coverage: raw?.coverage || fallback?.coverage || null,
    at: raw?.at,
    sourceFingerprint: raw?.sourceFingerprint,
  }, 0);
}

export function hasContextRecommendations(recs) {
  return Boolean(
    recs?.geminiQuestion
    || recs?.geminiQuestionCandidates?.length
    || recs?.searchQueries?.length
    || recs?.searchQueryCandidates?.length
    || recs?.searchQuery
    || recs?.progressSummary
    || recs?.workingConclusion
    || recs?.validationQueue?.length
    || recs?.blockers?.length
    || recs?.nextActions?.length
  );
}

/** Saved Do-next pack from a prior refresh — including steps-only results. */
export function hasPersistedDoNextPack(recs) {
  if (!recs || typeof recs !== 'object') return false;
  if (hasContextRecommendations(recs)) return true;
  const goals = recs.stepCandidates;
  if (!Array.isArray(goals) || !goals.length) return false;
  return goals.some(item => String(item?.goal || item || '').trim());
}

export function buildContextRecommendationFingerprint({
  pinCount,
  aiPinCount,
  frameCount,
  goal,
  description,
  currentStep,
  boardGoal,
  recentIntent,
  locale,
} = {}) {
  return {
    pinCount: Number(pinCount) || 0,
    aiPinCount: Number(aiPinCount) || 0,
    frameCount: Number(frameCount) || 0,
    currentStep: String(currentStep || goal || ''),
    boardGoal: String(boardGoal || description || ''),
    recentIntent: String(recentIntent || ''),
    locale: String(locale || 'en'),
  };
}

export function withContextRecommendationMeta(recs, context) {
  if (!recs) return recs;
  return {
    ...recs,
    at: recs.at || new Date().toISOString(),
    sourceFingerprint: buildContextRecommendationFingerprint(context),
  };
}

/**
 * True when clicking Do next refresh would meaningfully update coach next steps.
 * Mode / locale / description still auto-refresh. Dropdown step picks swap
 * per-step child packs from the last Do-next refresh (no AI).
 *
 * Cases:
 * - current step drifted from the last coach fingerprint
 * - pin / AI-pin count drifted from the last coach fingerprint
 * - user tried a Search / Ask destination since last coach content
 * - user marked a gap done / not-interested since last coach content
 * - all gaps closed via soft progress
 * - all validation probes finished without a coach revise
 * - epistemic type distribution changed since last refresh
 *   (collected pins plus text / image / AI-pin labels)
 */
export function doNextRefreshWouldMatter({
  fingerprint = null,
  pinCount = 0,
  aiPinCount = 0,
  frameCount = 0,
  goal = '',
  currentStep = '',
  triedActionCount = 0,
  softProgressCount = 0,
  blockersOpen = 0,
  blockersTotal = 0,
  validationsOpen = 0,
  validationsTotal = 0,
  needsStepRefresh = false,
  epistemicTypeChanged = false,
} = {}) {
  if (needsStepRefresh) return true;
  if (epistemicTypeChanged) return true;
  if (fingerprint) {
    if (readFingerprintCurrentStep(fingerprint) !== String(currentStep || goal || '')) return true;
    if (Number(fingerprint.pinCount || 0) !== Number(pinCount || 0)) return true;
    if (Number(fingerprint.aiPinCount || 0) !== Number(aiPinCount || 0)) return true;
    if (Number(fingerprint.frameCount || 0) !== Number(frameCount || 0)) return true;
  }
  if (Number(triedActionCount || 0) > 0) return true;

  // New done / not-interested marks added since the last AI refresh.
  if (Number(softProgressCount || 0) > 0) return true;

  // All gaps exhausted via soft progress (done / not-interested).
  if (Number(blockersTotal || 0) > 0 && Number(blockersOpen || 0) === 0) return true;

  // All validation probes finished without a coach revise.
  if (Number(validationsTotal || 0) > 0 && Number(validationsOpen || 0) === 0) return true;
  return false;
}

export function canShowMoreContextRecommendations(recs) {
  return getContextRecommendationBatchCount(recs) > 1;
}
